import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Link from "@mui/material/Link"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useCallback, useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import MeetingHistory from "./MeetingHistory"
import MeetingStatusChip from "./MeetingStatusChip"
import {
  browserTimeZone,
  formatMeetingDateTime,
  getApiErrorMessage,
  isReauthorizeError,
  localInputToIso,
  meetingCandidateNames,
  providerLabel,
} from "./meetingUtils"

const MODE_VIEW = "view"
const MODE_RESCHEDULE = "reschedule"
const MODE_CANCEL = "cancel"

export function canChangeMeeting(meeting) {
  return (
    Boolean(meeting) &&
    ["upcoming", "rescheduled"].includes(meeting.display_status) &&
    ["gmail", "ms"].includes(meeting.provider)
  )
}

function DetailRow({ label, children }) {
  return (
    <Column gap={0.25}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
        {children || "—"}
      </Typography>
    </Column>
  )
}

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
}

export default function MeetingDetailsDialog({
  open,
  meetingId,
  onClose,
  onChanged,
}) {
  const [meeting, setMeeting] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [mode, setMode] = useState(MODE_VIEW)
  const [newDateTime, setNewDateTime] = useState("")
  const [reason, setReason] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  const loadMeeting = useCallback(() => {
    if (!meetingId) return Promise.resolve()
    setIsLoading(true)
    return adminApi
      .fetchMeeting(meetingId)
      .then(({ data }) => setMeeting(data))
      .catch((error) =>
        setErrorMessage(
          getApiErrorMessage(error, "Could not load the meeting.")
        )
      )
      .finally(() => setIsLoading(false))
  }, [meetingId])

  useEffect(() => {
    if (!open) return
    setMeeting(null)
    setMode(MODE_VIEW)
    setNewDateTime("")
    setReason("")
    setErrorMessage("")
    loadMeeting()
  }, [open, loadMeeting])

  function switchMode(nextMode) {
    setMode(nextMode)
    setReason("")
    setErrorMessage("")
  }

  function handleSubmit() {
    setErrorMessage("")
    const trimmedReason = reason.trim()
    if (trimmedReason.length < 3) {
      setErrorMessage("Enter a reason (at least 3 characters).")
      return
    }
    let request
    if (mode === MODE_RESCHEDULE) {
      const iso = localInputToIso(newDateTime)
      if (!iso) {
        setErrorMessage("Pick the new date and time.")
        return
      }
      request = adminApi.rescheduleMeeting(meeting.id, {
        new_datetime: iso,
        reason: trimmedReason,
        timezone: browserTimeZone(),
      })
    } else {
      request = adminApi.cancelMeeting(meeting.id, { reason: trimmedReason })
    }
    setIsSaving(true)
    request
      .then(({ data }) => {
        setMeeting(data)
        setMode(MODE_VIEW)
        onChanged?.(data)
      })
      .catch((error) => {
        const message = getApiErrorMessage(
          error,
          "The change could not be saved."
        )
        setErrorMessage(
          isReauthorizeError(error)
            ? `${message} Nothing was changed.`
            : message
        )
      })
      .finally(() => setIsSaving(false))
  }

  const changeable = canChangeMeeting(meeting)

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{meeting?.title || "Meeting"}</DialogTitle>
      <DialogContent dividers>
        {isLoading && !meeting ? (
          <Row sx={{ justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Row>
        ) : null}
        {errorMessage ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        ) : null}
        {meeting ? (
          <Column gap={2}>
            <Row gap={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <MeetingStatusChip status={meeting.display_status} />
              <Typography variant="body2" color="text.secondary">
                {providerLabel(meeting.provider)} calendar · booked via{" "}
                {meeting.source}
              </Typography>
            </Row>
            {meeting.calendar_sync_status === "failed" ? (
              <Alert severity="warning">
                The last calendar update failed: {meeting.calendar_sync_error}
              </Alert>
            ) : null}
            <DetailRow label="When (your time zone)">
              {formatMeetingDateTime(meeting.start_datetime)}
            </DetailRow>
            <DetailRow label="User (calendar owner)">
              {meeting.host
                ? `${meeting.host.name} (${meeting.host.email})`
                : null}
            </DetailRow>
            <DetailRow label="Candidate">
              {meetingCandidateNames(meeting)}
            </DetailRow>
            <MeetingHistory history={meeting.history} />
            <DetailRow label="Booking form / notes">
              {meeting.description}
            </DetailRow>
            {meeting.event_link ? (
              <Link
                href={meeting.event_link}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
              >
                Open in calendar
              </Link>
            ) : null}
            {mode !== MODE_VIEW ? (
              <Column gap={1.5}>
                <Typography variant="subtitle2">
                  {mode === MODE_RESCHEDULE
                    ? "Reschedule meeting"
                    : "Cancel meeting"}
                </Typography>
                {mode === MODE_RESCHEDULE ? (
                  <TextField
                    label="New date and time"
                    type="datetime-local"
                    size="small"
                    value={newDateTime}
                    onChange={(event) => setNewDateTime(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText={`In your time zone (${browserTimeZone()})`}
                  />
                ) : null}
                <TextField
                  label="Reason"
                  size="small"
                  multiline
                  minRows={2}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  helperText="Shared with the candidate in the calendar update."
                />
              </Column>
            ) : null}
          </Column>
        ) : null}
      </DialogContent>
      <DialogActions>
        {mode === MODE_VIEW ? (
          <>
            {changeable ? (
              <>
                <Button onClick={() => switchMode(MODE_CANCEL)} color="error">
                  Cancel meeting
                </Button>
                <Button onClick={() => switchMode(MODE_RESCHEDULE)}>
                  Reschedule
                </Button>
              </>
            ) : null}
            <Button onClick={onClose} variant="contained">
              Close
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => switchMode(MODE_VIEW)} disabled={isSaving}>
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color={mode === MODE_CANCEL ? "error" : "primary"}
              disabled={isSaving}
            >
              {isSaving
                ? "Saving..."
                : mode === MODE_RESCHEDULE
                ? "Confirm new time"
                : "Confirm cancellation"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

MeetingDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  meetingId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
}
